import { useEffect, useState } from "react";
import { heavy, tap } from "../components/haptics";

/**
 * Produksjonen under tidskoden, og veien til aa bytte den.
 *
 * Linja sier HVOR du logger. Den er ogsaa det raskeste stedet aa bytte fra,
 * fordi det er der spoersmaalet oppstaar: ser du «Privat» naar du skulle
 * skrevet paa produksjonen, skal rettingen vaere ett trykk unna og ikke en
 * tur gjennom menyen.
 *
 * Velgeren finnes dermed PAA TO STEDER — her og i menyen. Det er med vilje:
 * menyen er flata man setter opp produksjonen i, denne er snarveien midt i
 * arbeidet. Begge kaller samme handling.
 *
 * Flata henger ned FRA toppstripa, med runding bare i bunnen og samme
 * bakgrunnsfarge som skallet. Den skal lese som at stripa ble hoeyere, ikke
 * som et kort foran appen.
 *
 * Toppstripa staar stille: skuffen er absolutt posisjonert og roerer ikke
 * layouten. Det kravet staar over alt annet her.
 */
export interface ProduksjonVelgerProps {
  readonly navn: string;
  /** «Blar tilbake», eller null. Staar ved siden av navnet. */
  readonly tillegg: string | null;
  readonly produksjoner: readonly { readonly id: string; readonly navn: string }[];
  readonly aktivId: string;
  onVelg(id: string): void;
}

export function ProduksjonVelger({
  navn, tillegg, produksjoner, aktivId, onVelg,
}: ProduksjonVelgerProps): JSX.Element {
  const [aapen, setAapen] = useState(false);

  /*
   * Escape lukker. Panelet er ikke en flate man arbeider i, og da skal den
   * raskeste veien ut ogsaa finnes for et tastatur — regi kan ha et koblet
   * til i kontrollrommet.
   */
  useEffect(() => {
    if (!aapen) return;
    const paaTast = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setAapen(false);
    };
    window.addEventListener("keydown", paaTast);
    return () => window.removeEventListener("keydown", paaTast);
  }, [aapen]);

  const velg = (id: string): void => {
    setAapen(false);
    if (id === aktivId) return;
    heavy();
    onVelg(id);
  };

  return (
    <>
      <button
        type="button"
        className={`mono undertc__linje prodvelger__knapp${
          tillegg === null ? "" : " undertc__linje--medHale"}`}
        aria-expanded={aapen}
        aria-haspopup="menu"
        onClick={() => { tap(); setAapen(!aapen); }}
      >
        {tillegg === null ? (
          <>
            {navn}
            {/*
              * Tegnet som SVG og ikke som et skrifttegn.
              *
              * «⌄» sto som en liten hake i feil stoerrelse og feil vekt, fordi
              * den arver fra en monospace-font som ikke er tegnet for det.
              * Her er den samme strek uansett skrift og skjerm.
              */}
            <svg
              className={`prodvelger__pil${aapen ? " prodvelger__pil--opp" : ""}`}
              viewBox="0 0 10 6"
              width="9"
              height="5.5"
              aria-hidden="true"
            >
              <path
                d="M1 1.2 5 4.8 9 1.2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </>
        ) : (
          <>
            {/* Navnet forkortes, halen aldri. Se surfaces.css. */}
            <span className="undertc__navn">{navn}</span>
            <span className="undertc__hale">{tillegg}</span>
          </>
        )}
      </button>

      {aapen ? (
        /* Fanger trykk utenfor. Ingen demping og ingen uskarphet: flata er en
           forlengelse av toppstripa, ikke et overlegg over appen. */
        <button
          type="button"
          className="prodvelger__skjerm"
          aria-label="Lukk produksjonsvelgeren"
          onClick={() => setAapen(false)}
        />
      ) : null}

      <div
        className={`prodvelger__skuff${aapen ? " prodvelger__skuff--aapen" : ""}`}
        aria-hidden={!aapen}
      >
        {/* To lag: skuffen animerer hoeyden, panelet klipper innholdet. */}
        <div className="prodvelger__panel" role="menu">
          {produksjoner.map((p) => (
            <button
              key={p.id}
              type="button"
              role="menuitem"
              className={`prodvelger__rad${
                p.id === aktivId ? " prodvelger__rad--valgt" : ""}`}
              tabIndex={aapen ? 0 : -1}
              onClick={() => velg(p.id)}
            >
              {p.navn}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
